/**
 * PDF Template Builder — Visual WYSIWYG Editor
 * Drag-and-drop field placement, live preview, and print-ready output.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, Download, Loader2, Trash2, Eye, GripVertical,
  ChevronDown, ChevronRight, Settings, ArrowLeft, Printer, Type,
  AlignLeft, AlignCenter, AlignRight, Move, Search, X, Copy, Edit3,
  LayoutTemplate, Columns, Maximize2, Palette, Image, Square, Minus,
  SeparatorHorizontal, BoxSelect
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Slider } from '@/components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { apiClient } from '@/services/apiClient';
import { PageHeader, PageContainer, PageState } from '@/components/weavein';

// ============================================================================
// TYPES
// ============================================================================

interface PDFTemplate {
  id: number;
  template_name: string;
  template_type: string;
  state_code: string | null;
  page_size: string;
  orientation: string;
  layout_config: any;
  header_config: any;
  footer_config: any;
  field_mappings: any;
  margins: any;
  background_pdf_url: string | null;
  is_active: boolean;
  created_at: string;
}

interface TemplateField {
  id: string; // client-side unique id
  name: string;
  label: string;
  x: number;
  y: number;
  font_size: number;
  alignment?: 'left' | 'center' | 'right';
  bold?: boolean;
  mapping?: string; // field_mappings value
  border?: 'none' | 'bottom' | 'box' | 'top';
  border_color?: string;
  border_width?: number;
  background_color?: string;
  text_color?: string;
  width?: number;
  padding?: number;
}

interface BrandingConfig {
  use_org_branding: boolean;
  show_logo: boolean;
  show_org_name: boolean;
  show_header_line: boolean;
  show_footer_line: boolean;
  show_page_border: boolean;
  show_page_numbers: boolean;
  primary_color?: string;
  accent_color?: string;
  border_color?: string;
  border_width?: number;
}

interface OrgBranding {
  brand_name?: string;
  brand_tagline?: string;
  brand_primary_color?: string;
  brand_secondary_color?: string;
  brand_accent_color?: string;
  brand_logo_url?: string;
  document_header_logo_url?: string;
  document_footer_text?: string;
  document_letterhead_url?: string;
  brand_font_family?: string;
}

interface TemplateSection {
  id: string;
  label: string;
  y: number;
  style: 'line' | 'accent_bar' | 'box' | 'none';
  line_color?: string;
  line_width?: number;
  accent_bar_width?: number;
  accent_color?: string;
  background_color?: string;
  height?: number;
  font_size?: number;
  label_color?: string;
}

interface AvailableField {
  name: string;
  label: string;
  category: string;
  type: string;
  is_custom?: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const TEMPLATE_TYPES = [
  { value: 'assessment', label: 'Assessment' },
  { value: 'care_plan', label: 'Care Plan' },
  { value: 'consent', label: 'Consent Form' },
  { value: 'letter', label: 'Letter' },
  { value: 'report', label: 'Report' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'custom', label: 'Custom' },
];

const PAGE_SIZES: Record<string, { width: number; height: number; label: string }> = {
  letter: { width: 612, height: 792, label: 'Letter (8.5" x 11")' },
  A4: { width: 595, height: 842, label: 'A4 (210mm x 297mm)' },
};

const DEFAULT_MARGINS = { top: 72, bottom: 72, left: 72, right: 72 };
const PREVIEW_SCALE = 0.75; // scale factor for the preview panel
const FIELD_LINE_HEIGHT = 20; // default line height in points

// ============================================================================
// UTILITY
// ============================================================================

function generateId() {
  return `f_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function groupBy<T>(items: T[], key: (item: T) => string): Record<string, T[]> {
  return items.reduce((acc, item) => {
    const k = key(item);
    (acc[k] = acc[k] || []).push(item);
    return acc;
  }, {} as Record<string, T[]>);
}

// ============================================================================
// FIELD PALETTE (left panel)
// ============================================================================

function FieldPalette({
  availableFields,
  loading,
  onAddField,
}: {
  availableFields: AvailableField[];
  loading: boolean;
  onAddField: (field: AvailableField) => void;
}) {
  const [search, setSearch] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  const filtered = availableFields.filter(
    f =>
      f.label.toLowerCase().includes(search.toLowerCase()) ||
      f.name.toLowerCase().includes(search.toLowerCase())
  );

  const grouped = groupBy(filtered, f => f.category);

  const toggleCategory = (cat: string) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-ink-400" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search fields..."
          className="h-8 pl-8 text-xs"
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-2 top-2">
            <X className="h-3.5 w-3.5 text-ink-400 hover:text-ink-500" />
          </button>
        )}
      </div>

      <div className="space-y-1 max-h-[calc(100vh-320px)] overflow-y-auto pr-1">
        {Object.entries(grouped).map(([category, fields]) => {
          const isExpanded = expandedCategories[category] !== false; // default expanded
          return (
            <div key={category}>
              <button
                onClick={() => toggleCategory(category)}
                className="flex items-center gap-1 w-full text-left px-1 py-1.5 text-xs font-semibold text-ink-500 hover:text-ink uppercase tracking-wider"
              >
                {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                {category}
                <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0">{fields.length}</Badge>
              </button>
              {isExpanded && (
                <div className="space-y-0.5 ml-1">
                  {fields.map(field => (
                    <button
                      key={field.name}
                      onClick={() => onAddField(field)}
                      className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded text-xs hover:bg-blue-50 hover:text-blue-700 transition-colors group"
                      title={`Add "${field.label}" to template`}
                    >
                      <Type className="h-3 w-3 text-ink-400 group-hover:text-blue-500 flex-shrink-0" />
                      <span className="truncate flex-1">{field.label}</span>
                      {field.is_custom && (
                        <Badge variant="outline" className="text-[9px] px-1 py-0 flex-shrink-0">custom</Badge>
                      )}
                      <Plus className="h-3 w-3 text-ink-300 group-hover:text-blue-500 flex-shrink-0 opacity-0 group-hover:opacity-100" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {Object.keys(grouped).length === 0 && (
          <p className="text-xs text-ink-400 text-center py-4">No fields match your search</p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// FIELD LIST (reorderable, center-left)
// ============================================================================

function FieldList({
  fields,
  selectedFieldId,
  onSelect,
  onReorder,
  onRemove,
  onDuplicate,
}: {
  fields: TemplateField[];
  selectedFieldId: string | null;
  onSelect: (id: string) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onRemove: (id: string) => void;
  onDuplicate: (id: string) => void;
}) {
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);

  const handleDragStart = (index: number) => {
    dragItem.current = index;
  };

  const handleDragEnter = (index: number) => {
    dragOverItem.current = index;
  };

  const handleDragEnd = () => {
    if (dragItem.current !== null && dragOverItem.current !== null && dragItem.current !== dragOverItem.current) {
      onReorder(dragItem.current, dragOverItem.current);
    }
    dragItem.current = null;
    dragOverItem.current = null;
  };

  if (fields.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-ink-400">
        <LayoutTemplate className="h-8 w-8 mb-2 opacity-40" />
        <p className="text-xs">Add fields from the palette</p>
        <p className="text-[10px]">or drag them onto the preview</p>
      </div>
    );
  }

  return (
    <div className="space-y-1 max-h-[calc(100vh-380px)] overflow-y-auto pr-1">
      {fields.map((field, index) => (
        <div
          key={field.id}
          draggable
          onDragStart={() => handleDragStart(index)}
          onDragEnter={() => handleDragEnter(index)}
          onDragEnd={handleDragEnd}
          onDragOver={e => e.preventDefault()}
          onClick={() => onSelect(field.id)}
          className={`flex items-center gap-1.5 px-2 py-2 rounded cursor-pointer transition-colors group ${
            selectedFieldId === field.id
              ? 'bg-blue-100 border border-blue-300'
              : 'hover:bg-paper border border-transparent'
          }`}
        >
          <GripVertical className="h-3.5 w-3.5 text-ink-300 cursor-grab flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium truncate">{field.label}</div>
            <div className="text-[10px] text-ink-400 truncate">{field.name} · {field.font_size}pt</div>
          </div>
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
            <button
              onClick={e => { e.stopPropagation(); onDuplicate(field.id); }}
              className="p-0.5 rounded hover:bg-paper-2"
              title="Duplicate"
            >
              <Copy className="h-3 w-3 text-ink-400" />
            </button>
            <button
              onClick={e => { e.stopPropagation(); onRemove(field.id); }}
              className="p-0.5 rounded hover:bg-red-100"
              title="Remove"
            >
              <Trash2 className="h-3 w-3 text-red-400" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// FIELD PROPERTIES PANEL
// ============================================================================

function FieldProperties({
  field,
  onChange,
}: {
  field: TemplateField;
  onChange: (updates: Partial<TemplateField>) => void;
}) {
  return (
    <div className="space-y-3 p-3 bg-paper rounded-lg">
      <h4 className="text-xs font-semibold text-ink-500 uppercase tracking-wider">Field Properties</h4>
      <div className="space-y-2">
        <div>
          <Label className="text-[10px]">Display Label</Label>
          <Input
            value={field.label}
            onChange={e => onChange({ label: e.target.value })}
            className="h-7 text-xs"
          />
        </div>
        <div>
          <Label className="text-[10px]">Data Field Name</Label>
          <Input
            value={field.name}
            onChange={e => onChange({ name: e.target.value })}
            className="h-7 text-xs font-mono"
          />
        </div>
        <div>
          <Label className="text-[10px]">Field Mapping (optional)</Label>
          <Input
            value={field.mapping || ''}
            onChange={e => onChange({ mapping: e.target.value || undefined })}
            className="h-7 text-xs font-mono"
            placeholder="e.g. member.first_name"
          />
        </div>

        {/* Typography */}
        <div className="pt-1 border-t">
          <Label className="text-[10px] font-semibold text-ink-500">Typography</Label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px]">Font Size ({field.font_size}pt)</Label>
            <Slider
              value={[field.font_size]}
              onValueChange={([v]) => onChange({ font_size: v })}
              min={6}
              max={28}
              step={1}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-[10px]">Alignment</Label>
            <div className="flex gap-1 mt-1">
              {[
                { value: 'left' as const, icon: AlignLeft },
                { value: 'center' as const, icon: AlignCenter },
                { value: 'right' as const, icon: AlignRight },
              ].map(({ value, icon: Icon }) => (
                <button
                  key={value}
                  onClick={() => onChange({ alignment: value })}
                  className={`p-1.5 rounded ${
                    (field.alignment || 'left') === value
                      ? 'bg-blue-100 text-blue-600'
                      : 'hover:bg-paper-2 text-ink-400'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={field.bold || false}
              onChange={e => onChange({ bold: e.target.checked })}
              className="rounded"
            />
            Bold
          </label>
          <div className="flex items-center gap-1">
            <Label className="text-[10px]">Color</Label>
            <input
              type="color"
              value={field.text_color || '#1f2937'}
              onChange={e => onChange({ text_color: e.target.value })}
              className="w-6 h-5 border rounded cursor-pointer"
            />
          </div>
        </div>

        {/* Borders & Background */}
        <div className="pt-1 border-t">
          <Label className="text-[10px] font-semibold text-ink-500">Borders & Background</Label>
        </div>
        <div>
          <Label className="text-[10px]">Border Style</Label>
          <div className="flex gap-1 mt-1">
            {[
              { value: 'none' as const, label: 'None', icon: X },
              { value: 'bottom' as const, label: 'Bottom', icon: Minus },
              { value: 'top' as const, label: 'Top', icon: SeparatorHorizontal },
              { value: 'box' as const, label: 'Box', icon: Square },
            ].map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                onClick={() => onChange({ border: value })}
                className={`p-1.5 rounded text-[9px] flex flex-col items-center gap-0.5 ${
                  (field.border || 'none') === value
                    ? 'bg-blue-100 text-blue-600'
                    : 'hover:bg-paper-2 text-ink-400'
                }`}
                title={label}
              >
                <Icon className="h-3 w-3" />
              </button>
            ))}
          </div>
        </div>
        {field.border && field.border !== 'none' && (
          <div className="grid grid-cols-2 gap-2">
            <div className="flex items-center gap-1">
              <Label className="text-[10px]">Border Color</Label>
              <input
                type="color"
                value={field.border_color || '#d1d5db'}
                onChange={e => onChange({ border_color: e.target.value })}
                className="w-6 h-5 border rounded cursor-pointer"
              />
            </div>
            <div>
              <Label className="text-[10px]">Border Width</Label>
              <Input
                type="number"
                value={field.border_width || 0.5}
                onChange={e => onChange({ border_width: parseFloat(e.target.value) || 0.5 })}
                className="h-7 text-xs font-mono"
                step={0.5}
                min={0.25}
                max={4}
              />
            </div>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Label className="text-[10px]">Background</Label>
          <input
            type="color"
            value={field.background_color || '#ffffff'}
            onChange={e => onChange({ background_color: e.target.value === '#ffffff' ? undefined : e.target.value })}
            className="w-6 h-5 border rounded cursor-pointer"
          />
          {field.background_color && (
            <button
              onClick={() => onChange({ background_color: undefined })}
              className="text-[9px] text-red-400 hover:text-red-600"
            >
              clear
            </button>
          )}
        </div>
        <div>
          <Label className="text-[10px]">Field Width (pts, 0 = auto)</Label>
          <Input
            type="number"
            value={field.width || 0}
            onChange={e => onChange({ width: parseFloat(e.target.value) || undefined })}
            className="h-7 text-xs font-mono"
          />
        </div>

        {/* Position */}
        <div className="pt-1 border-t">
          <Label className="text-[10px] font-semibold text-ink-500">Position</Label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px]">X (pts)</Label>
            <Input
              type="number"
              value={field.x}
              onChange={e => onChange({ x: parseFloat(e.target.value) || 0 })}
              className="h-7 text-xs font-mono"
            />
          </div>
          <div>
            <Label className="text-[10px]">Y (pts)</Label>
            <Input
              type="number"
              value={field.y}
              onChange={e => onChange({ y: parseFloat(e.target.value) || 0 })}
              className="h-7 text-xs font-mono"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SECTION PROPERTIES PANEL
// ============================================================================

function SectionProperties({
  section,
  onChange,
}: {
  section: TemplateSection;
  onChange: (updates: Partial<TemplateSection>) => void;
}) {
  return (
    <div className="space-y-3 p-3 bg-paper rounded-lg">
      <h4 className="text-xs font-semibold text-ink-500 uppercase tracking-wider">Section Properties</h4>
      <div className="space-y-2">
        <div>
          <Label className="text-[10px]">Section Label</Label>
          <Input
            value={section.label}
            onChange={e => onChange({ label: e.target.value })}
            className="h-7 text-xs"
            placeholder="e.g. Demographics"
          />
        </div>

        {/* Style */}
        <div className="pt-1 border-t">
          <Label className="text-[10px] font-semibold text-ink-500">Section Style</Label>
        </div>
        <div className="grid grid-cols-2 gap-1 mt-1">
          {[
            { value: 'line' as const, label: 'Line', icon: Minus },
            { value: 'accent_bar' as const, label: 'Accent Bar', icon: SeparatorHorizontal },
            { value: 'box' as const, label: 'Box', icon: Square },
            { value: 'none' as const, label: 'None', icon: X },
          ].map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              onClick={() => onChange({ style: value })}
              className={`p-1.5 rounded text-[9px] flex items-center gap-1 ${
                section.style === value
                  ? 'bg-blue-100 text-blue-600 font-medium'
                  : 'hover:bg-paper-2 text-ink-500'
              }`}
            >
              <Icon className="h-3 w-3" />
              {label}
            </button>
          ))}
        </div>

        {/* Colors */}
        <div className="pt-1 border-t">
          <Label className="text-[10px] font-semibold text-ink-500">Colors</Label>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-[10px] w-20">Label Color</Label>
          <input
            type="color"
            value={section.label_color || '#1a56db'}
            onChange={e => onChange({ label_color: e.target.value })}
            className="w-6 h-5 border rounded cursor-pointer"
          />
          <span className="text-[9px] text-ink-400 font-mono">{section.label_color || '#1a56db'}</span>
        </div>

        {section.style !== 'none' && (
          <div className="flex items-center gap-2">
            <Label className="text-[10px] w-20">{section.style === 'accent_bar' ? 'Bar Color' : 'Line Color'}</Label>
            <input
              type="color"
              value={section.style === 'accent_bar' ? (section.accent_color || '#1a56db') : (section.line_color || '#e5e7eb')}
              onChange={e => onChange(
                section.style === 'accent_bar'
                  ? { accent_color: e.target.value }
                  : { line_color: e.target.value }
              )}
              className="w-6 h-5 border rounded cursor-pointer"
            />
          </div>
        )}

        <div className="flex items-center gap-2">
          <Label className="text-[10px] w-20">Background</Label>
          <input
            type="color"
            value={section.background_color || '#ffffff'}
            onChange={e => onChange({ background_color: e.target.value === '#ffffff' ? undefined : e.target.value })}
            className="w-6 h-5 border rounded cursor-pointer"
          />
          {section.background_color && (
            <button
              onClick={() => onChange({ background_color: undefined })}
              className="text-[9px] text-red-400 hover:text-red-600"
            >
              clear
            </button>
          )}
        </div>

        {/* Size & Weight */}
        <div className="pt-1 border-t">
          <Label className="text-[10px] font-semibold text-ink-500">Size & Weight</Label>
        </div>
        {section.style !== 'none' && (
          <div>
            <Label className="text-[10px]">Line Width ({section.line_width || 0.75}px)</Label>
            <Slider
              value={[section.line_width || 0.75]}
              onValueChange={([v]) => onChange({ line_width: v })}
              min={0.25}
              max={4}
              step={0.25}
              className="mt-1"
            />
          </div>
        )}
        {section.style === 'accent_bar' && (
          <div>
            <Label className="text-[10px]">Bar Width ({section.accent_bar_width || 3}px)</Label>
            <Slider
              value={[section.accent_bar_width || 3]}
              onValueChange={([v]) => onChange({ accent_bar_width: v })}
              min={1}
              max={8}
              step={1}
              className="mt-1"
            />
          </div>
        )}
        <div>
          <Label className="text-[10px]">Section Height ({section.height || 18}pt)</Label>
          <Slider
            value={[section.height || 18]}
            onValueChange={([v]) => onChange({ height: v })}
            min={12}
            max={40}
            step={2}
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-[10px]">Label Font Size ({section.font_size || 9}pt)</Label>
          <Slider
            value={[section.font_size || 9]}
            onValueChange={([v]) => onChange({ font_size: v })}
            min={6}
            max={16}
            step={1}
            className="mt-1"
          />
        </div>

        {/* Position */}
        <div className="pt-1 border-t">
          <Label className="text-[10px] font-semibold text-ink-500">Position</Label>
        </div>
        <div>
          <Label className="text-[10px]">Y Position (pts)</Label>
          <Input
            type="number"
            value={section.y}
            onChange={e => onChange({ y: parseFloat(e.target.value) || 0 })}
            className="h-7 text-xs font-mono"
          />
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// LIVE PREVIEW PANEL
// ============================================================================

function LivePreview({
  fields,
  sections,
  headerConfig,
  footerConfig,
  pageSize,
  orientation,
  margins,
  selectedFieldId,
  selectedSectionId,
  onSelectField,
  onSelectSection,
  onDropField,
  onMoveField,
  scale,
  brandingConfig,
  orgBranding,
}: {
  fields: TemplateField[];
  sections: TemplateSection[];
  headerConfig: { title?: string; subtitle?: string };
  footerConfig: { text?: string };
  pageSize: string;
  orientation: string;
  margins: typeof DEFAULT_MARGINS;
  selectedFieldId: string | null;
  selectedSectionId: string | null;
  onSelectField: (id: string | null) => void;
  onSelectSection: (id: string | null) => void;
  onDropField: (fieldName: string, x: number, y: number) => void;
  onMoveField: (fieldId: string, x: number, y: number) => void;
  scale: number;
  brandingConfig?: BrandingConfig;
  orgBranding?: OrgBranding;
}) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [draggingFieldId, setDraggingFieldId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const dims = PAGE_SIZES[pageSize] || PAGE_SIZES.letter;
  const pWidth = orientation === 'landscape' ? dims.height : dims.width;
  const pHeight = orientation === 'landscape' ? dims.width : dims.height;

  const scaledW = pWidth * scale;
  const scaledH = pHeight * scale;

  // Convert PDF coords (origin bottom-left) to screen coords (origin top-left)
  const pdfToScreen = (x: number, y: number) => ({
    left: x * scale,
    top: (pHeight - y) * scale,
  });

  // Convert screen coords to PDF coords
  const screenToPdf = (left: number, top: number) => ({
    x: Math.round(left / scale),
    y: Math.round(pHeight - top / scale),
  });

  const handleCanvasClick = (e: React.MouseEvent) => {
    if (e.target === canvasRef.current) {
      onSelectField(null);
      onSelectSection(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const fieldName = e.dataTransfer.getData('text/plain');
    if (!fieldName || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const left = e.clientX - rect.left;
    const top = e.clientY - rect.top;
    const { x, y } = screenToPdf(left, top);
    onDropField(fieldName, x, y);
  };

  const handleFieldMouseDown = (e: React.MouseEvent, fieldId: string) => {
    e.stopPropagation();
    const field = fields.find(f => f.id === fieldId);
    if (!field || !canvasRef.current) return;

    onSelectField(fieldId);
    onSelectSection(null);
    setDraggingFieldId(fieldId);

    const rect = canvasRef.current.getBoundingClientRect();
    const screenPos = pdfToScreen(field.x, field.y);
    setDragOffset({
      x: e.clientX - rect.left - screenPos.left,
      y: e.clientY - rect.top - screenPos.top,
    });
  };

  useEffect(() => {
    if (!draggingFieldId) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const left = e.clientX - rect.left - dragOffset.x;
      const top = e.clientY - rect.top - dragOffset.y;
      const { x, y } = screenToPdf(left, top);
      onMoveField(draggingFieldId, Math.max(0, Math.min(x, pWidth)), Math.max(0, Math.min(y, pHeight)));
    };

    const handleMouseUp = () => {
      setDraggingFieldId(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingFieldId, dragOffset, pWidth, pHeight, scale]);

  return (
    <div className="flex flex-col items-center">
      <div
        ref={canvasRef}
        className="relative bg-surface border border-v2border shadow-lg overflow-hidden"
        style={{ width: scaledW, height: scaledH }}
        onClick={handleCanvasClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Page border */}
        {brandingConfig?.show_page_border && (
          <div
            className="absolute pointer-events-none"
            style={{
              left: (margins.left - 4) * scale,
              top: (margins.top - 4) * scale,
              width: (pWidth - margins.left - margins.right + 8) * scale,
              height: (pHeight - margins.top - margins.bottom + 8) * scale,
              border: `${(brandingConfig.border_width || 1) * scale}px solid ${brandingConfig.border_color || orgBranding?.brand_primary_color || '#1a56db'}`,
            }}
          />
        )}

        {/* Margin guides */}
        <div
          className="absolute border border-dashed border-v2border pointer-events-none"
          style={{
            left: margins.left * scale,
            top: margins.top * scale,
            width: (pWidth - margins.left - margins.right) * scale,
            height: (pHeight - margins.top - margins.bottom) * scale,
          }}
        />

        {/* Org name (top-right) */}
        {brandingConfig?.show_org_name && orgBranding?.brand_name && (
          <div
            className="absolute truncate pointer-events-none font-bold"
            style={{
              right: margins.right * scale,
              top: (margins.top - 12) * scale,
              fontSize: 10 * scale,
              lineHeight: 1,
              color: orgBranding.brand_primary_color || '#1a56db',
            }}
          >
            {orgBranding.brand_name}
          </div>
        )}

        {/* Logo */}
        {brandingConfig?.show_logo && (orgBranding?.document_header_logo_url || orgBranding?.brand_logo_url) && (
          <div
            className="absolute pointer-events-none"
            style={{
              left: margins.left * scale,
              top: margins.top * scale,
            }}
          >
            <img
              src={orgBranding.document_header_logo_url || orgBranding.brand_logo_url}
              alt="Logo"
              style={{ maxHeight: 32 * scale, maxWidth: 120 * scale, objectFit: 'contain' }}
              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          </div>
        )}

        {/* Header */}
        {headerConfig.title && (
          <div
            className="absolute font-bold truncate pointer-events-none"
            style={{
              ...pdfToScreen(margins.left, pHeight - (brandingConfig?.show_logo ? 90 : 50)),
              fontSize: 16 * scale,
              lineHeight: 1,
              color: brandingConfig?.primary_color || orgBranding?.brand_primary_color || '#000',
            }}
          >
            {headerConfig.title}
          </div>
        )}
        {headerConfig.subtitle && (
          <div
            className="absolute text-ink-500 truncate pointer-events-none"
            style={{
              ...pdfToScreen(margins.left, pHeight - (brandingConfig?.show_logo ? 108 : 68)),
              fontSize: 10 * scale,
              lineHeight: 1,
            }}
          >
            {headerConfig.subtitle}
          </div>
        )}

        {/* Header separator line */}
        {brandingConfig?.show_header_line && (headerConfig.title || (brandingConfig.show_logo && orgBranding?.brand_logo_url)) && (
          <div
            className="absolute pointer-events-none"
            style={{
              left: margins.left * scale,
              top: (brandingConfig.show_logo ? margins.top + 58 : margins.top + 42) * scale,
              width: (pWidth - margins.left - margins.right) * scale,
              height: 1.5 * scale,
              backgroundColor: brandingConfig.primary_color || orgBranding?.brand_primary_color || '#1a56db',
            }}
          />
        )}

        {/* Footer separator line */}
        {brandingConfig?.show_footer_line && footerConfig.text && (
          <div
            className="absolute pointer-events-none"
            style={{
              left: margins.left * scale,
              bottom: (margins.bottom + 16) * scale,
              width: (pWidth - margins.left - margins.right) * scale,
              height: 0.75 * scale,
              backgroundColor: brandingConfig.accent_color || orgBranding?.brand_accent_color || '#e5e7eb',
            }}
          />
        )}

        {/* Page number */}
        {brandingConfig?.show_page_numbers && (
          <div
            className="absolute pointer-events-none"
            style={{
              right: margins.right * scale,
              bottom: (margins.bottom + 4) * scale,
              fontSize: 8 * scale,
              lineHeight: 1,
              color: '#9ca3af',
            }}
          >
            Page 1
          </div>
        )}

        {/* Sections */}
        {sections.map(section => {
          const secPos = pdfToScreen(margins.left, section.y + (section.height || 18));
          const secWidth = (pWidth - margins.left - margins.right) * scale;
          const secHeight = ((section.height || 18) + 4) * scale;
          const isSecSelected = selectedSectionId === section.id;
          const lineW = (section.line_width || 0.75) * scale;

          return (
            <div
              key={section.id}
              className={`absolute cursor-pointer transition-shadow ${
                isSecSelected ? 'ring-2 ring-purple-500 ring-offset-1 z-10' : 'hover:ring-1 hover:ring-purple-300'
              }`}
              style={{
                left: secPos.left,
                top: secPos.top,
                width: secWidth,
                height: secHeight,
                backgroundColor: section.background_color || 'transparent',
                ...(section.style === 'box' ? {
                  border: `${lineW}px solid ${section.line_color || '#e5e7eb'}`,
                } : {}),
                ...(section.style === 'line' ? {
                  borderBottom: `${lineW}px solid ${section.line_color || '#e5e7eb'}`,
                } : {}),
              }}
              onClick={e => { e.stopPropagation(); onSelectSection(section.id); onSelectField(null); }}
            >
              {section.style === 'accent_bar' && (
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: (section.accent_bar_width || 3) * scale,
                    height: '100%',
                    backgroundColor: section.accent_color || '#1a56db',
                  }}
                />
              )}
              {section.label && (
                <div
                  className="font-bold uppercase pointer-events-none truncate"
                  style={{
                    fontSize: (section.font_size || 9) * scale,
                    lineHeight: 1,
                    color: section.label_color || '#1a56db',
                    paddingLeft: section.style === 'accent_bar' ? ((section.accent_bar_width || 3) + 6) * scale : 2 * scale,
                    paddingTop: 4 * scale,
                  }}
                >
                  {section.label}
                </div>
              )}
            </div>
          );
        })}

        {/* Fields */}
        {fields.map(field => {
          const pos = pdfToScreen(field.x, field.y);
          const isSelected = selectedFieldId === field.id;
          const isDragging = draggingFieldId === field.id;
          const fieldBorder = field.border && field.border !== 'none';
          const borderColor = field.border_color || '#d1d5db';
          const borderW = (field.border_width || 0.5) * scale;
          const fieldW = field.width ? field.width * scale : undefined;

          const borderStyle: React.CSSProperties = {};
          if (fieldBorder) {
            if (field.border === 'box') {
              borderStyle.border = `${borderW}px solid ${borderColor}`;
            } else if (field.border === 'bottom') {
              borderStyle.borderBottom = `${borderW}px solid ${borderColor}`;
            } else if (field.border === 'top') {
              borderStyle.borderTop = `${borderW}px solid ${borderColor}`;
            }
          }

          return (
            <div
              key={field.id}
              className={`absolute cursor-move select-none whitespace-nowrap transition-shadow ${
                isSelected ? 'ring-2 ring-blue-500 ring-offset-1 z-10' : 'hover:ring-1 hover:ring-blue-300'
              } ${isDragging ? 'opacity-70' : ''}`}
              style={{
                left: pos.left,
                top: pos.top,
                fontSize: field.font_size * scale,
                fontWeight: field.bold ? 700 : 400,
                textAlign: field.alignment || 'left',
                lineHeight: 1,
                padding: `${(field.padding || 2) * scale}px`,
                borderRadius: 2,
                backgroundColor: field.background_color || (isSelected ? 'rgba(59, 130, 246, 0.08)' : 'transparent'),
                color: field.text_color || '#1f2937',
                width: fieldW,
                ...borderStyle,
              }}
              onMouseDown={e => handleFieldMouseDown(e, field.id)}
            >
              <span style={{ color: field.text_color || '#374151' }}>{field.label}:</span>{' '}
              <span className="italic" style={{ color: field.text_color ? `${field.text_color}88` : '#93c5fd' }}>{'{{' + field.name + '}}'}</span>
            </div>
          );
        })}

        {/* Footer */}
        {footerConfig.text && (
          <div
            className="absolute text-ink-400 truncate pointer-events-none"
            style={{
              left: margins.left * scale,
              bottom: 10 * scale,
              fontSize: 8 * scale,
              lineHeight: 1,
            }}
          >
            {footerConfig.text}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// TEMPLATE EDITOR (full builder)
// ============================================================================

function TemplateEditor({
  template,
  onSave,
  onCancel,
  saving,
}: {
  template: Partial<PDFTemplate> | null; // null = new template
  onSave: (data: any) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const isEditing = !!template?.id;

  // Template metadata
  const [name, setName] = useState(template?.template_name || '');
  const [type, setType] = useState(template?.template_type || 'assessment');
  const [stateCode, setStateCode] = useState(template?.state_code || '');
  const [pageSize, setPageSize] = useState(template?.page_size || 'letter');
  const [orientation, setOrientation] = useState(template?.orientation || 'portrait');
  const [margins, setMargins] = useState(template?.margins || { ...DEFAULT_MARGINS });
  const [headerTitle, setHeaderTitle] = useState(template?.header_config?.title || '');
  const [headerSubtitle, setHeaderSubtitle] = useState(template?.header_config?.subtitle || '');
  const [footerText, setFooterText] = useState(template?.footer_config?.text || '');

  // Fields
  const [fields, setFields] = useState<TemplateField[]>(() => {
    const layoutFields = template?.layout_config?.fields || [];
    const mappings = template?.field_mappings || {};
    return layoutFields.map((f: any, i: number) => ({
      id: generateId(),
      name: f.name,
      label: f.label || f.name,
      x: f.x ?? 72,
      y: f.y ?? (700 - i * FIELD_LINE_HEIGHT),
      font_size: f.font_size ?? 11,
      alignment: f.alignment || 'left',
      bold: f.bold || false,
      mapping: mappings[f.name] || undefined,
      border: f.border,
      border_color: f.border_color,
      border_width: f.border_width,
      background_color: f.background_color,
      text_color: f.text_color,
      width: f.width,
      padding: f.padding,
    }));
  });

  // Sections
  const [sections, setSections] = useState<TemplateSection[]>(() => {
    const layoutSections = template?.layout_config?.sections || [];
    return layoutSections.map((s: any) => ({
      id: generateId(),
      label: s.label || '',
      y: s.y ?? 600,
      style: s.style || 'line',
      line_color: s.line_color,
      line_width: s.line_width,
      accent_bar_width: s.accent_bar_width,
      accent_color: s.accent_color,
      background_color: s.background_color,
      height: s.height,
      font_size: s.font_size,
      label_color: s.label_color,
    }));
  });

  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [previewScale, setPreviewScale] = useState(PREVIEW_SCALE);
  const [activeTab, setActiveTab] = useState('fields');

  // Branding
  const [brandingConfig, setBrandingConfig] = useState<BrandingConfig>(() => {
    const bc = template?.branding_config || {};
    return {
      use_org_branding: bc.use_org_branding || false,
      show_logo: bc.show_logo ?? true,
      show_org_name: bc.show_org_name ?? true,
      show_header_line: bc.show_header_line ?? true,
      show_footer_line: bc.show_footer_line ?? true,
      show_page_border: bc.show_page_border || false,
      show_page_numbers: bc.show_page_numbers ?? true,
      primary_color: bc.primary_color || '',
      accent_color: bc.accent_color || '',
      border_color: bc.border_color || '',
      border_width: bc.border_width || 1,
    };
  });
  const [orgBranding, setOrgBranding] = useState<OrgBranding>({});

  // Available fields from API
  const [availableFields, setAvailableFields] = useState<AvailableField[]>([]);
  const [fieldsLoading, setFieldsLoading] = useState(true);

  useEffect(() => {
    // Fetch org branding
    const fetchBranding = async () => {
      try {
        const resp = await apiClient.get('/api/pdf-templates/branding-preview');
        setOrgBranding(resp.data || {});
      } catch { /* branding not configured */ }
    };
    fetchBranding();
  }, []);

  useEffect(() => {
    const fetchFields = async () => {
      try {
        const resp = await apiClient.get('/api/pdf-templates/available-fields');
        const core = resp.data.core_fields || [];
        const custom = resp.data.custom_fields || [];
        setAvailableFields([...core, ...custom]);
      } catch {
        setAvailableFields([
          { name: 'first_name', label: 'First Name', category: 'Demographics', type: 'str' },
          { name: 'last_name', label: 'Last Name', category: 'Demographics', type: 'str' },
          { name: 'email', label: 'Email', category: 'Demographics', type: 'str' },
          { name: 'phone', label: 'Phone', category: 'Demographics', type: 'str' },
          { name: 'date_of_birth', label: 'Date of Birth', category: 'Demographics', type: 'date' },
          { name: 'medicaid_id', label: 'Medicaid ID', category: 'Demographics', type: 'str' },
        ]);
      } finally {
        setFieldsLoading(false);
      }
    };
    fetchFields();
  }, []);

  const selectedField = fields.find(f => f.id === selectedFieldId) || null;

  // Auto-calculate Y positions based on field order
  const recalculateYPositions = useCallback((fieldList: TemplateField[]) => {
    const dims = PAGE_SIZES[pageSize] || PAGE_SIZES.letter;
    const pHeight = orientation === 'landscape' ? dims.width : dims.height;
    const startY = pHeight - (margins.top || 72) - 100; // below header

    return fieldList.map((f, i) => ({
      ...f,
      y: startY - i * (f.font_size + 8),
    }));
  }, [pageSize, orientation, margins]);

  const addField = useCallback((available: AvailableField) => {
    const newField: TemplateField = {
      id: generateId(),
      name: available.name,
      label: available.label,
      x: margins.left || 72,
      y: 0, // will be recalculated
      font_size: 11,
      alignment: 'left',
      bold: false,
      mapping: available.is_custom ? available.name : undefined,
    };

    setFields(prev => {
      const updated = [...prev, newField];
      return recalculateYPositions(updated);
    });
  }, [margins, recalculateYPositions]);

  const addFieldAtPosition = useCallback((fieldName: string, x: number, y: number) => {
    // Find matching available field
    const available = availableFields.find(f => f.name === fieldName);
    const newField: TemplateField = {
      id: generateId(),
      name: fieldName,
      label: available?.label || fieldName,
      x,
      y,
      font_size: 11,
      alignment: 'left',
      bold: false,
      mapping: available?.is_custom ? fieldName : undefined,
    };
    setFields(prev => [...prev, newField]);
    setSelectedFieldId(newField.id);
  }, [availableFields]);

  const updateField = useCallback((id: string, updates: Partial<TemplateField>) => {
    setFields(prev => prev.map(f => (f.id === id ? { ...f, ...updates } : f)));
  }, []);

  const removeField = useCallback((id: string) => {
    setFields(prev => {
      const updated = prev.filter(f => f.id !== id);
      return recalculateYPositions(updated);
    });
    if (selectedFieldId === id) setSelectedFieldId(null);
  }, [selectedFieldId, recalculateYPositions]);

  const duplicateField = useCallback((id: string) => {
    setFields(prev => {
      const idx = prev.findIndex(f => f.id === id);
      if (idx === -1) return prev;
      const source = prev[idx];
      const dupe: TemplateField = {
        ...source,
        id: generateId(),
        label: source.label + ' (copy)',
        y: source.y - FIELD_LINE_HEIGHT,
      };
      const updated = [...prev];
      updated.splice(idx + 1, 0, dupe);
      return updated;
    });
  }, []);

  const reorderFields = useCallback((fromIndex: number, toIndex: number) => {
    setFields(prev => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return recalculateYPositions(updated);
    });
  }, [recalculateYPositions]);

  const moveFieldOnCanvas = useCallback((fieldId: string, x: number, y: number) => {
    setFields(prev => prev.map(f => (f.id === fieldId ? { ...f, x, y } : f)));
  }, []);

  // Section management
  const selectedSection = sections.find(s => s.id === selectedSectionId) || null;

  const addSection = useCallback(() => {
    const dims = PAGE_SIZES[pageSize] || PAGE_SIZES.letter;
    const pH = orientation === 'landscape' ? dims.width : dims.height;
    const yPos = pH - (margins.top || 72) - 100 - sections.length * 60;
    const newSection: TemplateSection = {
      id: generateId(),
      label: `Section ${sections.length + 1}`,
      y: Math.max(yPos, margins.bottom || 72),
      style: 'line',
      line_color: '#e5e7eb',
      line_width: 0.75,
      height: 18,
      font_size: 9,
      label_color: '#1a56db',
    };
    setSections(prev => [...prev, newSection]);
    setSelectedSectionId(newSection.id);
    setSelectedFieldId(null);
  }, [pageSize, orientation, margins, sections.length]);

  const updateSection = useCallback((id: string, updates: Partial<TemplateSection>) => {
    setSections(prev => prev.map(s => (s.id === id ? { ...s, ...updates } : s)));
  }, []);

  const removeSection = useCallback((id: string) => {
    setSections(prev => prev.filter(s => s.id !== id));
    if (selectedSectionId === id) setSelectedSectionId(null);
  }, [selectedSectionId]);

  const duplicateSection = useCallback((id: string) => {
    setSections(prev => {
      const source = prev.find(s => s.id === id);
      if (!source) return prev;
      const dupe: TemplateSection = {
        ...source,
        id: generateId(),
        label: source.label + ' (copy)',
        y: source.y - 40,
      };
      return [...prev, dupe];
    });
  }, []);

  const handleSave = () => {
    const layoutFields = fields.map(f => ({
      name: f.name,
      label: f.label,
      x: f.x,
      y: f.y,
      font_size: f.font_size,
      alignment: f.alignment,
      bold: f.bold,
      border: f.border,
      border_color: f.border_color,
      border_width: f.border_width,
      background_color: f.background_color,
      text_color: f.text_color,
      width: f.width,
      padding: f.padding,
    }));

    const fieldMappings: Record<string, string> = {};
    fields.forEach(f => {
      if (f.mapping) fieldMappings[f.name] = f.mapping;
    });

    onSave({
      template_name: name,
      template_type: type,
      state_code: stateCode || null,
      page_size: pageSize,
      orientation,
      margins,
      layout_config: {
        fields: layoutFields,
        sections: sections.map(s => ({
          label: s.label,
          y: s.y,
          style: s.style,
          line_color: s.line_color,
          line_width: s.line_width,
          accent_bar_width: s.accent_bar_width,
          accent_color: s.accent_color,
          background_color: s.background_color,
          height: s.height,
          font_size: s.font_size,
          label_color: s.label_color,
        })),
      },
      header_config: { title: headerTitle || null, subtitle: headerSubtitle || null },
      footer_config: { text: footerText || null },
      field_mappings: Object.keys(fieldMappings).length > 0 ? fieldMappings : {},
      branding_config: brandingConfig,
    });
  };

  return (
    <div className="flex flex-col h-[calc(100vh-120px)]">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-surface">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="h-5 w-px bg-paper-2" />
          <Input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Template name..."
            className="h-8 w-64 text-sm font-medium border-transparent hover:border-v2border focus:border-blue-300"
          />
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="h-8 w-36 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TEMPLATE_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 text-xs text-ink-500 mr-2">
            <Maximize2 className="h-3 w-3" />
            <Slider
              value={[previewScale * 100]}
              onValueChange={([v]) => setPreviewScale(v / 100)}
              min={40}
              max={120}
              step={5}
              className="w-20"
            />
            <span className="w-8 text-right">{Math.round(previewScale * 100)}%</span>
          </div>
          <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={!name || saving}>
            {saving ? <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Saving...</> : isEditing ? 'Update Template' : 'Create Template'}
          </Button>
        </div>
      </div>

      {/* Main editor area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left panel: field palette + field list */}
        <div className="w-64 border-r bg-gray-50/50 flex flex-col overflow-hidden">
          <Tabs defaultValue="fields" value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 overflow-hidden">
            <TabsList className="mx-2 mt-2 grid grid-cols-4">
              <TabsTrigger value="fields" className="text-xs">Fields</TabsTrigger>
              <TabsTrigger value="sections" className="text-xs">Sections</TabsTrigger>
              <TabsTrigger value="settings" className="text-xs">Settings</TabsTrigger>
              <TabsTrigger value="branding" className="text-xs">Branding</TabsTrigger>
            </TabsList>

            <TabsContent value="fields" className="flex-1 overflow-hidden flex flex-col px-2 py-2 gap-3">
              <div>
                <h3 className="text-xs font-semibold text-ink-500 mb-1 uppercase tracking-wider">Available Fields</h3>
                <FieldPalette
                  availableFields={availableFields}
                  loading={fieldsLoading}
                  onAddField={addField}
                />
              </div>
              <div className="border-t pt-2">
                <h3 className="text-xs font-semibold text-ink-500 mb-1 uppercase tracking-wider flex items-center justify-between">
                  Template Fields
                  <Badge variant="secondary" className="text-[10px]">{fields.length}</Badge>
                </h3>
                <FieldList
                  fields={fields}
                  selectedFieldId={selectedFieldId}
                  onSelect={setSelectedFieldId}
                  onReorder={reorderFields}
                  onRemove={removeField}
                  onDuplicate={duplicateField}
                />
              </div>
            </TabsContent>

            <TabsContent value="sections" className="flex-1 overflow-y-auto px-2 py-2 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-semibold text-ink-500 uppercase tracking-wider">Sections</h3>
                <Button variant="outline" size="sm" className="h-6 text-[10px] px-2" onClick={addSection}>
                  <Plus className="h-3 w-3 mr-1" /> Add Section
                </Button>
              </div>
              {sections.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-ink-400">
                  <SeparatorHorizontal className="h-8 w-8 mb-2 opacity-40" />
                  <p className="text-xs">No sections yet</p>
                  <p className="text-[10px] mt-1">Add sections to organize fields with borders & labels</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {sections.map(section => (
                    <div
                      key={section.id}
                      onClick={() => { setSelectedSectionId(section.id); setSelectedFieldId(null); }}
                      className={`flex items-center gap-1.5 px-2 py-2 rounded cursor-pointer transition-colors group ${
                        selectedSectionId === section.id
                          ? 'bg-purple-100 border border-purple-300'
                          : 'hover:bg-paper border border-transparent'
                      }`}
                    >
                      <div className="flex-shrink-0">
                        {section.style === 'line' && <Minus className="h-3.5 w-3.5 text-ink-400" />}
                        {section.style === 'accent_bar' && <SeparatorHorizontal className="h-3.5 w-3.5 text-ink-400" />}
                        {section.style === 'box' && <Square className="h-3.5 w-3.5 text-ink-400" />}
                        {section.style === 'none' && <Type className="h-3.5 w-3.5 text-ink-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium truncate">{section.label || '(untitled)'}</div>
                        <div className="text-[10px] text-ink-400">{section.style} · y={section.y}</div>
                      </div>
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
                        <button
                          onClick={e => { e.stopPropagation(); duplicateSection(section.id); }}
                          className="p-0.5 rounded hover:bg-paper-2"
                          title="Duplicate"
                        >
                          <Copy className="h-3 w-3 text-ink-400" />
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); removeSection(section.id); }}
                          className="p-0.5 rounded hover:bg-red-100"
                          title="Remove"
                        >
                          <Trash2 className="h-3 w-3 text-red-400" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="settings" className="flex-1 overflow-y-auto px-2 py-2 space-y-3">
              <div>
                <Label className="text-[10px]">State Code</Label>
                <Input value={stateCode} onChange={e => setStateCode(e.target.value)} placeholder="CA" maxLength={2} className="h-7 text-xs" />
              </div>
              <div>
                <Label className="text-[10px]">Page Size</Label>
                <Select value={pageSize} onValueChange={setPageSize}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PAGE_SIZES).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px]">Orientation</Label>
                <Select value={orientation} onValueChange={setOrientation}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="portrait">Portrait</SelectItem>
                    <SelectItem value="landscape">Landscape</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px]">Header Title</Label>
                <Input value={headerTitle} onChange={e => setHeaderTitle(e.target.value)} placeholder="Assessment Form" className="h-7 text-xs" />
              </div>
              <div>
                <Label className="text-[10px]">Header Subtitle</Label>
                <Input value={headerSubtitle} onChange={e => setHeaderSubtitle(e.target.value)} placeholder="California State Form" className="h-7 text-xs" />
              </div>
              <div>
                <Label className="text-[10px]">Footer Text</Label>
                <Input value={footerText} onChange={e => setFooterText(e.target.value)} placeholder="Page 1 of 1 — Confidential" className="h-7 text-xs" />
              </div>
              <div>
                <Label className="text-[10px] font-semibold">Margins (points)</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {(['top', 'right', 'bottom', 'left'] as const).map(side => (
                    <div key={side}>
                      <Label className="text-[9px] capitalize">{side}</Label>
                      <Input
                        type="number"
                        value={margins[side]}
                        onChange={e => setMargins({ ...margins, [side]: parseInt(e.target.value) || 0 })}
                        className="h-7 text-xs font-mono"
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-xs"
                  onClick={() => setFields(recalculateYPositions(fields))}
                >
                  <Columns className="h-3 w-3 mr-1" /> Auto-Arrange Fields
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="branding" className="flex-1 overflow-y-auto px-2 py-2 space-y-3">
              {/* Org branding toggle */}
              <div className="p-2 bg-blue-50 rounded-lg">
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={brandingConfig.use_org_branding}
                    onChange={e => setBrandingConfig({ ...brandingConfig, use_org_branding: e.target.checked })}
                    className="rounded"
                  />
                  <span className="font-medium">Use Organization Branding</span>
                </label>
                {orgBranding.brand_name && (
                  <p className="text-[10px] text-blue-600 mt-1 ml-5">
                    From: {orgBranding.brand_name}
                  </p>
                )}
              </div>

              {/* Logo */}
              <div>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={brandingConfig.show_logo}
                    onChange={e => setBrandingConfig({ ...brandingConfig, show_logo: e.target.checked })}
                    className="rounded"
                  />
                  Show Logo in Header
                </label>
                {brandingConfig.show_logo && (orgBranding.document_header_logo_url || orgBranding.brand_logo_url) && (
                  <div className="mt-2 p-2 bg-paper rounded border">
                    <img
                      src={orgBranding.document_header_logo_url || orgBranding.brand_logo_url}
                      alt="Org logo"
                      className="max-h-8 max-w-[120px] object-contain"
                      onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  </div>
                )}
                {brandingConfig.show_logo && !orgBranding.brand_logo_url && !orgBranding.document_header_logo_url && (
                  <p className="text-[10px] text-ink-400 mt-1">
                    No logo configured. Set one in Admin &gt; Organization Branding.
                  </p>
                )}
              </div>

              {/* Org name */}
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={brandingConfig.show_org_name}
                  onChange={e => setBrandingConfig({ ...brandingConfig, show_org_name: e.target.checked })}
                  className="rounded"
                />
                Show Org Name in Header
              </label>

              {/* Lines */}
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={brandingConfig.show_header_line}
                  onChange={e => setBrandingConfig({ ...brandingConfig, show_header_line: e.target.checked })}
                  className="rounded"
                />
                Header Separator Line
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={brandingConfig.show_footer_line}
                  onChange={e => setBrandingConfig({ ...brandingConfig, show_footer_line: e.target.checked })}
                  className="rounded"
                />
                Footer Separator Line
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={brandingConfig.show_page_numbers}
                  onChange={e => setBrandingConfig({ ...brandingConfig, show_page_numbers: e.target.checked })}
                  className="rounded"
                />
                Page Numbers
              </label>

              {/* Page border */}
              <div className="pt-2 border-t">
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={brandingConfig.show_page_border}
                    onChange={e => setBrandingConfig({ ...brandingConfig, show_page_border: e.target.checked })}
                    className="rounded"
                  />
                  <BoxSelect className="h-3 w-3" /> Page Border
                </label>
                {brandingConfig.show_page_border && (
                  <div className="ml-5 mt-2 space-y-2">
                    <div className="flex items-center gap-2">
                      <Label className="text-[10px]">Color</Label>
                      <input
                        type="color"
                        value={brandingConfig.border_color || '#1a56db'}
                        onChange={e => setBrandingConfig({ ...brandingConfig, border_color: e.target.value })}
                        className="w-6 h-5 border rounded cursor-pointer"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px]">Width ({brandingConfig.border_width || 1}px)</Label>
                      <Slider
                        value={[brandingConfig.border_width || 1]}
                        onValueChange={([v]) => setBrandingConfig({ ...brandingConfig, border_width: v })}
                        min={0.5}
                        max={4}
                        step={0.5}
                        className="mt-1"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Colors override */}
              <div className="pt-2 border-t">
                <Label className="text-[10px] font-semibold text-ink-500">Color Overrides</Label>
                <p className="text-[9px] text-ink-400 mb-2">Leave empty to use org branding colors</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label className="text-[10px] w-16">Primary</Label>
                    <input
                      type="color"
                      value={brandingConfig.primary_color || orgBranding.brand_primary_color || '#1a56db'}
                      onChange={e => setBrandingConfig({ ...brandingConfig, primary_color: e.target.value })}
                      className="w-6 h-5 border rounded cursor-pointer"
                    />
                    <span className="text-[9px] text-ink-400 font-mono">
                      {brandingConfig.primary_color || orgBranding.brand_primary_color || '#1a56db'}
                    </span>
                    {brandingConfig.primary_color && (
                      <button onClick={() => setBrandingConfig({ ...brandingConfig, primary_color: '' })} className="text-[9px] text-red-400">reset</button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-[10px] w-16">Accent</Label>
                    <input
                      type="color"
                      value={brandingConfig.accent_color || orgBranding.brand_accent_color || '#e5e7eb'}
                      onChange={e => setBrandingConfig({ ...brandingConfig, accent_color: e.target.value })}
                      className="w-6 h-5 border rounded cursor-pointer"
                    />
                    <span className="text-[9px] text-ink-400 font-mono">
                      {brandingConfig.accent_color || orgBranding.brand_accent_color || '#e5e7eb'}
                    </span>
                    {brandingConfig.accent_color && (
                      <button onClick={() => setBrandingConfig({ ...brandingConfig, accent_color: '' })} className="text-[9px] text-red-400">reset</button>
                    )}
                  </div>
                </div>
              </div>

              {/* Branding preview summary */}
              {orgBranding.brand_name && (
                <div className="pt-2 border-t">
                  <Label className="text-[10px] font-semibold text-ink-500">Org Branding Summary</Label>
                  <div className="mt-1 p-2 bg-paper rounded text-[10px] space-y-0.5">
                    <div><span className="text-ink-400">Name:</span> {orgBranding.brand_name}</div>
                    {orgBranding.brand_tagline && <div><span className="text-ink-400">Tagline:</span> {orgBranding.brand_tagline}</div>}
                    {orgBranding.brand_primary_color && (
                      <div className="flex items-center gap-1">
                        <span className="text-ink-400">Primary:</span>
                        <span className="w-3 h-3 rounded" style={{ backgroundColor: orgBranding.brand_primary_color }} />
                        <span className="font-mono">{orgBranding.brand_primary_color}</span>
                      </div>
                    )}
                    {orgBranding.document_footer_text && <div><span className="text-ink-400">Footer:</span> {orgBranding.document_footer_text}</div>}
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Center: Live preview */}
        <div className="flex-1 overflow-auto bg-paper-2 flex justify-center py-6 px-4">
          <LivePreview
            fields={fields}
            sections={sections}
            headerConfig={{ title: headerTitle, subtitle: headerSubtitle }}
            footerConfig={{ text: footerText }}
            pageSize={pageSize}
            orientation={orientation}
            margins={margins}
            selectedFieldId={selectedFieldId}
            selectedSectionId={selectedSectionId}
            onSelectField={setSelectedFieldId}
            onSelectSection={setSelectedSectionId}
            onDropField={addFieldAtPosition}
            onMoveField={moveFieldOnCanvas}
            scale={previewScale}
            brandingConfig={brandingConfig}
            orgBranding={orgBranding}
          />
        </div>

        {/* Right panel: field/section properties */}
        <div className="w-60 border-l bg-gray-50/50 overflow-y-auto p-2">
          {selectedField ? (
            <FieldProperties
              field={selectedField}
              onChange={updates => updateField(selectedField.id, updates)}
            />
          ) : selectedSection ? (
            <SectionProperties
              section={selectedSection}
              onChange={updates => updateSection(selectedSection.id, updates)}
            />
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-ink-400 text-xs">
              <Move className="h-6 w-6 mb-2 opacity-40" />
              <p>Select a field or section</p>
              <p className="text-[10px] mt-1">Click on an item in the list or preview</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// GENERATE DIALOG
// ============================================================================

function GenerateDialog({
  template,
  open,
  onClose,
}: {
  template: PDFTemplate;
  open: boolean;
  onClose: () => void;
}) {
  const isInvoice = template.template_type === 'invoice';
  const [sourceType, setSourceType] = useState(isInvoice ? 'billing_period' : 'custom');
  const [sourceId, setSourceId] = useState('');
  const [customData, setCustomData] = useState('{\n  "first_name": "John",\n  "last_name": "Doe",\n  "date_of_birth": "01/15/1990"\n}');
  const [generating, setGenerating] = useState(false);
  const [billingPeriods, setBillingPeriods] = useState<any[]>([]);
  const [periodsLoaded, setPeriodsLoaded] = useState(false);

  // Load billing periods for invoice templates
  React.useEffect(() => {
    if (isInvoice && open && !periodsLoaded) {
      apiClient.get('/api/pmpm/billing-periods').then(r => {
        setBillingPeriods(r.data || []);
        setPeriodsLoaded(true);
      }).catch(() => setPeriodsLoaded(true));
    }
  }, [isInvoice, open, periodsLoaded]);

  const generate = async () => {
    setGenerating(true);
    try {
      if (sourceType === 'billing_period' && sourceId) {
        // Use the PMPM invoice PDF generator directly (produces formatted invoice)
        const token = localStorage.getItem('auth_token');
        const cboId = localStorage.getItem('active_cbo_id');
        const resp = await fetch(`/api/pmpm/billing-periods/${sourceId}/invoice-pdf`, {
          headers: {
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            ...(cboId ? { 'X-CBO-ID': cboId } : {}),
          },
        });
        if (!resp.ok) throw new Error(await resp.text());
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      } else {
        // Use generic PDF template generator
        let body: any = { source_type: sourceType };
        if (sourceType === 'custom') {
          body.custom_data = JSON.parse(customData);
        } else {
          body.source_id = parseInt(sourceId);
        }

        const resp = await apiClient.post(`/api/pdf-templates/${template.id}/generate`, body, {
          responseType: 'blob',
        });
        const blob = new Blob([resp.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      }
    } catch (e: any) {
      alert(e.response?.data?.detail || e.message || 'PDF generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const fmtCurrency = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="h-4 w-4" /> {isInvoice ? 'Generate Invoice' : 'Generate PDF'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-ink-500">Generate from "{template.template_name}"</p>
          <div>
            <Label className="text-xs">Data Source</Label>
            <Select value={sourceType} onValueChange={setSourceType}>
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                {isInvoice && <SelectItem value="billing_period">PMPM Billing Period</SelectItem>}
                <SelectItem value="custom">Custom Data (JSON)</SelectItem>
                <SelectItem value="member">Member Record</SelectItem>
                <SelectItem value="form_instance">Form Instance</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {sourceType === 'billing_period' && (
            <div>
              <Label className="text-xs">Select Billing Period</Label>
              <Select value={sourceId} onValueChange={setSourceId}>
                <SelectTrigger className="h-8"><SelectValue placeholder="Select a billing period..." /></SelectTrigger>
                <SelectContent>
                  {billingPeriods.map((bp: any) => (
                    <SelectItem key={bp.id} value={String(bp.id)}>
                      {monthNames[bp.period_month - 1]} {bp.period_year} — {bp.eligible_member_count} members — {fmtCurrency(bp.total_amount || 0)}
                    </SelectItem>
                  ))}
                  {billingPeriods.length === 0 && periodsLoaded && (
                    <SelectItem value="" disabled>No billing periods found</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {billingPeriods.length > 0 && (
                <p className="text-xs text-ink-400 mt-1">Uses the formatted PMPM invoice layout with org branding and member roster.</p>
              )}
            </div>
          )}
          {sourceType !== 'custom' && sourceType !== 'billing_period' && (
            <div>
              <Label className="text-xs">Source ID</Label>
              <Input value={sourceId} onChange={e => setSourceId(e.target.value)} type="number" placeholder="ID" className="h-8" />
            </div>
          )}
          {sourceType === 'custom' && (
            <div>
              <Label className="text-xs">Custom Data (JSON)</Label>
              <Textarea value={customData} onChange={e => setCustomData(e.target.value)} rows={5} className="font-mono text-xs" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={generate} disabled={generating || (sourceType === 'billing_period' && !sourceId)}>
            {generating ? <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Generating...</> : <><Download className="h-3 w-3 mr-1" /> Generate & Open</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export default function PDFTemplatesPage() {
  const [templates, setTemplates] = useState<PDFTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editorMode, setEditorMode] = useState<'list' | 'create' | 'edit'>('list');
  const [editingTemplate, setEditingTemplate] = useState<PDFTemplate | null>(null);
  const [saving, setSaving] = useState(false);
  const [generateTemplate, setGenerateTemplate] = useState<PDFTemplate | null>(null);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const resp = await apiClient.get('/api/pdf-templates');
      setTemplates(resp.data || []);
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const saveTemplate = async (data: any) => {
    setSaving(true);
    try {
      if (editingTemplate?.id) {
        await apiClient.put(`/api/pdf-templates/${editingTemplate.id}`, data);
      } else {
        await apiClient.post('/api/pdf-templates', data);
      }
      setEditorMode('list');
      setEditingTemplate(null);
      await fetchTemplates();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (id: number) => {
    if (!window.confirm('Delete this template?')) return;
    await apiClient.delete(`/api/pdf-templates/${id}`);
    await fetchTemplates();
  };

  // Editor mode
  if (editorMode === 'create' || editorMode === 'edit') {
    return (
      <TemplateEditor
        template={editingTemplate}
        onSave={saveTemplate}
        onCancel={() => { setEditorMode('list'); setEditingTemplate(null); }}
        saving={saving}
      />
    );
  }

  const header = (
    <PageHeader
      eyebrow="Admin · Documents"
      title="PDF Templates"
      description="Create and manage PDF templates with the visual builder"
      actions={
        <button
          onClick={() => { setEditingTemplate(null); setEditorMode('create'); }}
          className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
        >
          <Plus className="h-3.5 w-3.5" /> New Template
        </button>
      }
    />
  );

  // List mode
  if (loading) {
    return (
      <div className="flex h-full flex-col">
        {header}
        <PageContainer>
          <PageState variant="loading" title="Loading templates..." />
        </PageContainer>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {header}
      <PageContainer>

      {templates.length === 0 ? (
        <PageState
          variant="empty"
          icon={<LayoutTemplate className="h-6 w-6 text-ink-300" />}
          title="No templates yet"
          message="Create your first PDF template using the visual builder"
          action={
            <button
              onClick={() => { setEditingTemplate(null); setEditorMode('create'); }}
              className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
            >
              <Plus className="h-3.5 w-3.5" /> Create Template
            </button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(tpl => (
            <Card key={tpl.id} className="hover:shadow-md transition-shadow group">
              <CardContent className="py-4">
                {/* Mini preview */}
                <div className="bg-paper rounded border mb-3 relative overflow-hidden" style={{ height: 120 }}>
                  <div className="absolute inset-2 bg-surface rounded shadow-sm border border-v2border-muted p-2">
                    {tpl.header_config?.title && (
                      <div className="text-[8px] font-bold truncate text-ink-700">{tpl.header_config.title}</div>
                    )}
                    {tpl.header_config?.subtitle && (
                      <div className="text-[6px] text-ink-400 truncate">{tpl.header_config.subtitle}</div>
                    )}
                    <div className="mt-1 space-y-0.5">
                      {(tpl.layout_config?.fields || []).slice(0, 5).map((f: any, i: number) => (
                        <div key={i} className="flex gap-1">
                          <div className="text-[6px] text-ink-500 truncate">{f.label}:</div>
                          <div className="text-[6px] text-blue-300 truncate">{'{{...}}'}</div>
                        </div>
                      ))}
                      {(tpl.layout_config?.fields?.length || 0) > 5 && (
                        <div className="text-[6px] text-ink-300">+{tpl.layout_config.fields.length - 5} more</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm truncate">{tpl.template_name}</h3>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <Badge variant="outline" className="text-[10px]">{tpl.template_type.replace('_', ' ')}</Badge>
                      {tpl.state_code && <Badge variant="secondary" className="text-[10px]">{tpl.state_code}</Badge>}
                      <span className="text-[10px] text-ink-400">
                        {tpl.layout_config?.fields?.length || 0} fields
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-1 mt-3 pt-3 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 text-xs h-7"
                    onClick={() => { setEditingTemplate(tpl); setEditorMode('edit'); }}
                  >
                    <Edit3 className="h-3 w-3 mr-1" /> Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => setGenerateTemplate(tpl)}
                  >
                    <Printer className="h-3 w-3 mr-1" /> Generate
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 h-7 w-7 p-0"
                    onClick={() => deleteTemplate(tpl.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Generate Dialog */}
      {generateTemplate && (
        <GenerateDialog
          template={generateTemplate}
          open={!!generateTemplate}
          onClose={() => setGenerateTemplate(null)}
        />
      )}
      </PageContainer>
    </div>
  );
}
